/* eslint-disable no-unused-vars */
/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiArrowRight, FiMinus, FiPlus } from 'react-icons/fi';
import AppSelect from '../../../../shared/components/app-select/AppSelect';
import useCountries from '../../hooks/useCountries';
import {
  DEFAULT_NATIONALITY,
  ONLINE_VISA_ROUTES,
  PAX_CONFIG,
  VISA_PURPOSE_OPTIONS,
  CHILD_AGE_OPTIONS,
  INFANT_AGE_OPTIONS,
} from '../../constants/onlineVisaConstants';
import { buildSearchQuery } from '../../utils/onlineVisaUtils';
import './VisaSearchForm.scss';

const padNumber = (n) => String(n).padStart(2, '0');

// Resize an ages array to the new count, preserving existing values.
const resizeAges = (current, nextCount) => {
  const arr = Array.isArray(current) ? [...current] : [];
  if (arr.length === nextCount) return arr;
  if (arr.length > nextCount) return arr.slice(0, nextCount);
  while (arr.length < nextCount) arr.push('');
  return arr;
};

function PaxRow({ label, value, min, max, onChange }) {
  const handleDec = () => onChange(Math.max(min, (value || 0) - 1));
  const handleInc = () => onChange(Math.min(max, (value || 0) + 1));
  return (
    <div className="visa-search-form__pax-row">
      <span className="visa-search-form__pax-label">{label}</span>
      <div className="visa-search-form__pax-controls">
        <button
          type="button"
          className="visa-search-form__pax-btn"
          onClick={handleDec}
          disabled={value <= min}
          aria-label={`decrement ${label}`}
        >
          <FiMinus />
        </button>
        <span className="visa-search-form__pax-value">{padNumber(value)}</span>
        <button
          type="button"
          className="visa-search-form__pax-btn"
          onClick={handleInc}
          disabled={value >= max}
          aria-label={`increment ${label}`}
        >
          <FiPlus />
        </button>
      </div>
    </div>
  );
}

PaxRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

function AgeRow({ label, value, options, onChange, error }) {
  return (
    <div className="visa-search-form__age-row">
      <span className="visa-search-form__age-label">{label}</span>
      <div className="visa-search-form__age-field">
        <AppSelect
          name={label}
          label="Select age"
          placeholder=""
          selectedValue={value == null || value === '' ? '' : String(value)}
          options={options}
          onSelect={(selected) => onChange(selected?.value || '')}
          isClearable={false}
          maxheight="200px"
        />
        {error && <small className="validation-error">{error}</small>}
      </div>
    </div>
  );
}

AgeRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
};

AgeRow.defaultProps = {
  value: '',
  error: '',
};

function VisaSearchForm({ initialValues, onSearch }) {
  const navigate = useNavigate();
  const {
    countries,
    options: countryOptions,
    loading: countriesLoading,
  } = useCountries();

  const [form, setForm] = useState({
    nationality: DEFAULT_NATIONALITY.countryCode,
    destinationCode: '',
    destinationName: '',
    visaPurpose: '',
    adults: PAX_CONFIG.MIN_ADULTS,
    children: 0,
    infants: 0,
    childAges: [],
    infantAges: [],
    ...initialValues,
  });
  const [errors, setErrors] = useState({});
  // const [editingDestination, setEditingDestination] = useState(false);

  useEffect(() => {
    if (initialValues) {
      setForm((prev) => ({
        ...prev,
        ...initialValues,
        childAges: resizeAges(
          initialValues.childAges || prev.childAges,
          initialValues.children != null
            ? initialValues.children
            : prev.children,
        ),
        infantAges: resizeAges(
          initialValues.infantAges || prev.infantAges,
          initialValues.infants != null ? initialValues.infants : prev.infants,
        ),
      }));
    }
  }, [initialValues]);

  // Keep the destination name in sync once countries resolve.
  useEffect(() => {
    if (!form.destinationCode || form.destinationName) return;
    const match = countries.find((c) => c.countryCode === form.destinationCode);
    if (match) {
      setForm((prev) => ({
        ...prev,
        destinationName: (match.countryName || '').toUpperCase(),
      }));
    }
  }, [countries, form.destinationCode, form.destinationName]);

  // const displayDestination = useMemo(() => {
  //   if (form.destinationName) {
  //     return form.destinationName
  //       .toLowerCase()
  //       .replace(/\b\w/g, (ch) => ch.toUpperCase());
  //   }
  //   if (form.destinationCode) {
  //     const match = countries.find(
  //       (c) => c.countryCode === form.destinationCode,
  //     );
  //     if (match) {
  //       return match.countryName
  //         .toLowerCase()
  //         .replace(/\b\w/g, (ch) => ch.toUpperCase());
  //     }
  //   }
  //   return '';
  // }, [form.destinationCode, form.destinationName, countries]);

  const totalPax =
    (form.adults || 0) + (form.children || 0) + (form.infants || 0);

  const handleNationalityChange = (selected) => {
    setForm((prev) => ({ ...prev, nationality: selected?.value || '' }));
    setErrors((e) => ({ ...e, nationality: undefined }));
  };

  const handleDestinationChange = (selected) => {
    setForm((prev) => ({
      ...prev,
      destinationCode: selected?.value || '',
      destinationName: (
        selected?.countryName ||
        selected?.label ||
        ''
      ).toUpperCase(),
    }));
    setErrors((e) => ({ ...e, destination: undefined }));
    // setEditingDestination(false);
  };

  const handleVisaTypeChange = (selected) => {
    setForm((prev) => ({ ...prev, visaPurpose: selected?.value || '' }));
  };

  const handlePaxChange = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'children') {
        next.childAges = resizeAges(prev.childAges, value);
      }
      if (key === 'infants') {
        next.infantAges = resizeAges(prev.infantAges, value);
      }
      return next;
    });
    // Clear any age validation errors when the count changes.
    if (key === 'children') {
      setErrors((e) => ({ ...e, childAges: undefined }));
    }
    if (key === 'infants') {
      setErrors((e) => ({ ...e, infantAges: undefined }));
    }
  };

  const handleChildAgeChange = (index, value) => {
    setForm((prev) => {
      const next = [...(prev.childAges || [])];
      next[index] = value;
      return { ...prev, childAges: next };
    });
    setErrors((e) => {
      if (!e.childAges) return e;
      const arr = [...e.childAges];
      arr[index] = undefined;
      return { ...e, childAges: arr };
    });
  };

  const handleInfantAgeChange = (index, value) => {
    setForm((prev) => {
      const next = [...(prev.infantAges || [])];
      next[index] = value;
      return { ...prev, infantAges: next };
    });
    setErrors((e) => {
      if (!e.infantAges) return e;
      const arr = [...e.infantAges];
      arr[index] = undefined;
      return { ...e, infantAges: arr };
    });
  };

  const handleSubmit = () => {
    const err = {};
    if (!form.nationality) err.nationality = 'Please select your nationality';
    if (!form.destinationCode) err.destination = 'Please select a destination';
    if (form.nationality === form.destinationCode) {
      err.destination = 'Destination must differ from nationality';
    }

    // Validate per-child ages.
    if (form.children > 0) {
      const childErrors = [];
      let hasChildError = false;
      for (let i = 0; i < form.children; i += 1) {
        const val = form.childAges?.[i];
        if (val === '' || val == null) {
          childErrors[i] = 'Select age';
          hasChildError = true;
        } else {
          childErrors[i] = undefined;
        }
      }
      if (hasChildError) err.childAges = childErrors;
    }

    // Validate per-infant ages.
    if (form.infants > 0) {
      const infantErrors = [];
      let hasInfantError = false;
      for (let i = 0; i < form.infants; i += 1) {
        const val = form.infantAges?.[i];
        if (val === '' || val == null) {
          infantErrors[i] = 'Select age';
          hasInfantError = true;
        } else {
          infantErrors[i] = undefined;
        }
      }
      if (hasInfantError) err.infantAges = infantErrors;
    }

    setErrors(err);
    if (Object.keys(err).length > 0) return;

    // Normalise ages to numeric for downstream use.
    const normalised = {
      ...form,
      childAges: (form.childAges || []).map((v) => Number(v)),
      infantAges: (form.infantAges || []).map((v) => Number(v)),
    };

    if (onSearch) {
      onSearch(normalised);
      return;
    }
    const qs = buildSearchQuery(normalised);
    navigate(`${ONLINE_VISA_ROUTES.OPTIONS}?${qs}`);
  };

  return (
    <section className="visa-search-form">
      {/* Destination headline */}
      <div className="visa-search-form__destination">
        <div className="visa-search-form__destination-text">
          <span className="visa-search-form__label">Select Destination</span>
          <div className="visa-search-form__destination-picker">
            <AppSelect
              name="destination"
              label=""
              placeholder={
                countriesLoading ? 'Loading countries…' : 'Choose a destination'
              }
              selectedValue={form.destinationCode}
              options={countryOptions}
              onSelect={(selected) => handleDestinationChange(selected)}
              isClearable={false}
              maxheight="260px"
            />
            {errors.destination && (
              <small className="validation-error">{errors.destination}</small>
            )}
          </div>
        </div>
      </div>

      <div className="visa-search-form__divider" />

      {/* Body: two columns */}
      <div className="visa-search-form__body">
        <div className="visa-search-form__col visa-search-form__col--left">
          <div className="visa-search-form__field">
            <AppSelect
              name="nationality"
              label="Nationality"
              placeholder={countriesLoading ? 'Loading…' : 'Nationality'}
              selectedValue={form.nationality}
              options={countryOptions}
              onSelect={(selected) => handleNationalityChange(selected)}
              isClearable={false}
              maxheight="260px"
            />
            {errors.nationality && (
              <small className="validation-error">{errors.nationality}</small>
            )}
          </div>
          <div className="visa-search-form__field">
            <AppSelect
              name="visaPurpose"
              label="Visa Type"
              placeholder="All Visa Types"
              selectedValue={form.visaPurpose}
              options={VISA_PURPOSE_OPTIONS}
              onSelect={(selected) => handleVisaTypeChange(selected)}
              isClearable
              maxheight="260px"
            />
          </div>
        </div>

        <div className="visa-search-form__col visa-search-form__col--right">
          <div className="visa-search-form__pax-card">
            <div className="visa-search-form__pax-header">
              <span className="visa-search-form__pax-title">
                No. of Travellers
              </span>
              <span className="visa-search-form__pax-total">
                {padNumber(totalPax)}
              </span>
            </div>
            <PaxRow
              label="Adult (13 Yrs & Above)"
              value={form.adults}
              min={PAX_CONFIG.MIN_ADULTS}
              max={PAX_CONFIG.MAX_ADULTS}
              onChange={(v) => handlePaxChange('adults', v)}
            />
            <PaxRow
              label="Child (2-12 Yrs)"
              value={form.children}
              min={PAX_CONFIG.MIN_CHILDREN}
              max={PAX_CONFIG.MAX_CHILDREN}
              onChange={(v) => handlePaxChange('children', v)}
            />

            {form.children > 0 && (
              <div className="visa-search-form__age-group">
                {Array.from({ length: form.children }).map((_, idx) => (
                  <AgeRow
                    // eslint-disable-next-line react/no-array-index-key
                    key={`child-${idx}`}
                    label={`Child ${idx + 1} Age`}
                    value={form.childAges?.[idx]}
                    options={CHILD_AGE_OPTIONS}
                    onChange={(v) => handleChildAgeChange(idx, v)}
                    error={errors.childAges?.[idx]}
                  />
                ))}
              </div>
            )}

            <PaxRow
              label="Infant (0-1 Yrs)"
              value={form.infants}
              min={PAX_CONFIG.MIN_INFANTS}
              max={PAX_CONFIG.MAX_INFANTS}
              onChange={(v) => handlePaxChange('infants', v)}
            />

            {form.infants > 0 && (
              <div className="visa-search-form__age-group">
                {Array.from({ length: form.infants }).map((_, idx) => (
                  <AgeRow
                    // eslint-disable-next-line react/no-array-index-key
                    key={`infant-${idx}`}
                    label={`Infant ${idx + 1} Age`}
                    value={form.infantAges?.[idx]}
                    options={INFANT_AGE_OPTIONS}
                    onChange={(v) => handleInfantAgeChange(idx, v)}
                    error={errors.infantAges?.[idx]}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <button
        type="button"
        className="visa-search-form__submit"
        onClick={handleSubmit}
      >
        <span>Search</span>
        <FiArrowRight />
      </button>
    </section>
  );
}

VisaSearchForm.propTypes = {
  initialValues: PropTypes.object,
  onSearch: PropTypes.func,
};

VisaSearchForm.defaultProps = {
  initialValues: null,
  onSearch: null,
};

export default VisaSearchForm;
